import { Component } from "react"
import { CheckCircle2, Coffee } from "lucide-react"
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler"
import StateConstants from "src/states/StateContstants"
import ApiHandler from "src/api/ApiHandler"
import { SubscriptionPlanKey, SubscriptionState, openBillingPortal, startSubscriptionCheckout } from "./Subscription"
import { PlanOffer, ensureReportPrices, formatReportPrice, planOffers } from "src/components/reports/reportPricing"
import { MONTHLY_PLAN, planPriceEur, trackSubscriptionPayFlow } from "./subscriptionPayFlowEvents"
import "./PremiumPage.css"
import "./SubscriptionPlan.css"


interface PlanContentProps {
    stateHandler: EventAdaptingStateHandler
    apiHandler: ApiHandler
    showSuccess?: boolean
    showCancelled?: boolean
    onPaymentComplete?: () => void
    variant?: "page" | "modal"
}

interface PlanContentState {
    isPremium: boolean
    accountType: string | null
    subscription: SubscriptionState | null
    isRedirecting: boolean
    error: string | null
    plans: PlanOffer[]
    selectedPlan: SubscriptionPlanKey
}

const PLAN_LABELS: Record<SubscriptionPlanKey, { name: string, per: string, cta: string, badge?: string }> = {
    week: { name: "Weekly", per: "/ week", cta: "Start weekly" },
    year: { name: "Yearly", per: "/ year", cta: "Start yearly", badge: "Most popular" },
    lifetime: { name: "Lifetime", per: "once", cta: "Get lifetime access", badge: "Best value" },
}

export const SUBSCRIPTION_UNLOCKS = [
    "Full estimate for any home",
    "5-year value forecast",
    "Nearby sales and estimates",
    "Area market insights",
]

export function formatPeriodEnd(epochSeconds: number | null): string | null {
    if (!epochSeconds) return null
    return new Intl.DateTimeFormat("en-IE", { day: "numeric", month: "short", year: "numeric" }).format(new Date(epochSeconds * 1000))
}


class PlanContent extends Component<PlanContentProps, PlanContentState> {
    constructor(props: PlanContentProps) {
        super(props)
        this.state = {
            isPremium: props.stateHandler.get(StateConstants.IS_PREMIUM) || false,
            accountType: props.stateHandler.get(StateConstants.ACCOUNT_TYPE) || null,
            subscription: props.stateHandler.get(StateConstants.SUBSCRIPTION) || null,
            isRedirecting: false,
            error: null,
            plans: planOffers(),
            selectedPlan: "year",
        }
    }

    private onIsPremium = (isPremium: boolean) => { this.setState({ isPremium: !!isPremium }) }
    private onAccountType = (accountType: string | null) => { this.setState({ accountType: accountType || null }) }
    private onSubscription = (subscription: SubscriptionState | null) => { this.setState({ subscription: subscription || null }) }

    componentDidMount() {
        if (!this.state.isPremium) {
            trackSubscriptionPayFlow("subscription_plans_viewed", {
                surface: this.props.variant || "page",
                plans_offered: this.state.plans.length > 0 ? this.state.plans.map(offer => offer.plan) : [MONTHLY_PLAN],
                is_logged_in: !!this.props.stateHandler.get(StateConstants.IS_LOGGED_IN),
            })
        }
        ensureReportPrices(() => this.props.apiHandler.getReportPriceTier()).then(() => this.setState({ plans: planOffers() }))
        this.props.stateHandler.addListener(StateConstants.IS_PREMIUM, this.onIsPremium)
        this.props.stateHandler.addListener(StateConstants.ACCOUNT_TYPE, this.onAccountType)
        this.props.stateHandler.addListener(StateConstants.SUBSCRIPTION, this.onSubscription)
    }

    componentWillUnmount() {
        this.props.stateHandler.removeListener(StateConstants.IS_PREMIUM, this.onIsPremium)
        this.props.stateHandler.removeListener(StateConstants.ACCOUNT_TYPE, this.onAccountType)
        this.props.stateHandler.removeListener(StateConstants.SUBSCRIPTION, this.onSubscription)
    }

    subscribe = async () => {
        const plan = this.state.plans.length > 0 ? this.state.selectedPlan : undefined
        trackSubscriptionPayFlow("subscription_pay_button_clicked", {
            surface: this.props.variant || "page",
            plan: plan ?? MONTHLY_PLAN,
            price_eur: planPriceEur(plan),
            is_logged_in: !!this.props.stateHandler.get(StateConstants.IS_LOGGED_IN),
        })
        this.setState({ isRedirecting: true, error: null })
        try {
            await startSubscriptionCheckout(this.props.apiHandler, this.props.stateHandler, `plan_${this.props.variant || "page"}`, undefined, plan)
        } catch (error) {
            console.error("Failed to start the subscription checkout", error)
            this.setState({ error: "We couldn't open the checkout just now. Try again in a moment." })
        } finally {
            this.setState({ isRedirecting: false })
        }
    }

    manage = async () => {
        this.setState({ isRedirecting: true, error: null })
        try {
            await openBillingPortal(this.props.apiHandler)
        } catch (error) {
            console.error("Failed to open the billing portal", error)
            this.setState({ error: "We couldn't open your subscription settings just now. Try again in a moment." })
        } finally {
            this.setState({ isRedirecting: false })
        }
    }

    renderActive() {
        if (this.state.accountType === "lifetime") {
            return (
                <div className="subscription-plan-status">
                    <span className="subscription-plan-status-pill is-active">Active</span>
                    <p className="subscription-plan-status-title">Lifetime Access</p>
                    <p className="subscription-plan-status-subtitle">Every figure is unlocked, for good.</p>
                </div>
            )
        }

        const subscription = this.state.subscription
        const periodEnd = formatPeriodEnd(subscription?.currentPeriodEnd ?? null)
        const isEnding = !!subscription?.cancelAtPeriodEnd
        const subtitle = isEnding
            ? (periodEnd ? `Every figure stays unlocked until ${periodEnd}.` : "Every figure stays unlocked until the end of this billing period.")
            : `Every figure is unlocked${periodEnd ? ` · Renews ${periodEnd}` : ""}.`

        return (
            <div className="subscription-plan-status">
                <span className={`subscription-plan-status-pill ${isEnding ? "is-cancelled" : "is-active"}`}>
                    {isEnding ? "Cancelled" : "Active"}
                </span>
                <p className="subscription-plan-status-title">HomeIndex Edge</p>
                <p className="subscription-plan-status-subtitle">{subtitle}</p>
                {subscription && (
                    <button type="button" className="subscription-plan-secondary" onClick={this.manage} disabled={this.state.isRedirecting}>
                        Manage subscription
                    </button>
                )}
            </div>
        )
    }

    renderPlans() {
        const { plans, selectedPlan } = this.state
        return (
            <article className="subscription-plan-card">
                <div className="subscription-plan-tiers" role="radiogroup" aria-label="Choose a plan">
                    {plans.map(offer => {
                        const label = PLAN_LABELS[offer.plan]
                        return (
                            <button
                                key={offer.plan}
                                type="button"
                                role="radio"
                                aria-checked={selectedPlan === offer.plan}
                                className={`subscription-plan-tier${selectedPlan === offer.plan ? " is-selected" : ""}`}
                                onClick={() => this.setState({ selectedPlan: offer.plan })}
                            >
                                {label.badge && <span className="subscription-plan-tier-badge">{label.badge}</span>}
                                <span className="subscription-plan-tier-name">{label.name}</span>
                                <strong>{formatReportPrice(offer.amount_eur)}</strong>
                                <span className="subscription-plan-tier-per">{label.per}</span>
                            </button>
                        )
                    })}
                </div>
                <p className="subscription-plan-coffee">
                    {PLAN_LABELS[selectedPlan].name === "Lifetime" ? "One payment, no renewals." : "Auto-renews · Cancel anytime"}
                </p>
                <ul className="subscription-plan-list">
                    {SUBSCRIPTION_UNLOCKS.map(item => (
                        <li key={item}><CheckCircle2 size={16} strokeWidth={2} aria-hidden /> {item}</li>
                    ))}
                </ul>
                <button type="button" className="subscription-plan-cta" onClick={this.subscribe} disabled={this.state.isRedirecting}>
                    {this.state.isRedirecting ? "Opening checkout…" : PLAN_LABELS[selectedPlan].cta}
                </button>
                <p className="subscription-plan-footnote">Secure checkout by Stripe</p>
            </article>
        )
    }

    renderOffer() {
        if (this.state.plans.length > 0) return this.renderPlans()
        return (
            <article className="subscription-plan-card">
                <p className="subscription-plan-price">
                    <strong>€5</strong> <span>/ month</span>
                </p>
                <p className="subscription-plan-coffee">
                    <Coffee size={16} strokeWidth={1.9} aria-hidden /> For the price of a coffee
                </p>
                <ul className="subscription-plan-list">
                    {SUBSCRIPTION_UNLOCKS.map(item => (
                        <li key={item}><CheckCircle2 size={16} strokeWidth={2} aria-hidden /> {item}</li>
                    ))}
                </ul>
                <button
                    type="button"
                    className="subscription-plan-cta"
                    onClick={this.subscribe}
                    disabled={this.state.isRedirecting}
                >
                    {this.state.isRedirecting ? "Opening checkout…" : "Checkout"}
                </button>
                <p className="subscription-plan-footnote">Secure checkout by Stripe</p>
            </article>
        )
    }

    render() {
        const isModal = (this.props.variant || "page") === "modal"

        return (
            <div className={`subscription-plan${isModal ? " subscription-plan--modal" : ""}`}>
                {this.props.showCancelled && (
                    <div className="premium-cancelled-banner">
                        Checkout was cancelled. You can subscribe anytime.
                    </div>
                )}
                <div className="subscription-plan-header">
                    <h1 className="subscription-plan-title">{this.state.isPremium ? "Your plan" : "HomeIndex Edge"}</h1>
                    <p className="subscription-plan-subtitle">
                        Get ahead of the market. See exact estimates and 5-year forecasts for any home in Ireland, before you buy, sell or bid.
                    </p>
                </div>
                {this.state.isPremium ? this.renderActive() : this.renderOffer()}
                {this.state.error && <p className="subscription-plan-error" role="alert">{this.state.error}</p>}
            </div>
        )
    }
}

export default PlanContent
